import React, { useState } from 'react';
import { Box, Typography, Button, Paper, CircularProgress, Alert } from '@mui/material';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const EmergencyReset = () => {
  const [isResetting, setIsResetting] = useState(false);
  const [serverResetStatus, setServerResetStatus] = useState(null);
  const [clientResetStatus, setClientResetStatus] = useState(null);
  const [error, setError] = useState(null);

  const performEmergencyReset = async () => {
    setIsResetting(true);
    setError(null);
    
    try {
      // Step 1: Reset server state
      setServerResetStatus('pending');
      
      try {
        // Call server-side emergency reset API
        const response = await fetch('/api/emergency-reset', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          // Add a cache-busting parameter
          cache: 'no-store'
        });
        
        if (!response.ok) {
          throw new Error(`Server returned ${response.status}`);
        }
        
        const result = await response.json();
        console.log('Server reset result:', result);
        setServerResetStatus('success');
      } catch (serverError) {
        console.error('Server reset error:', serverError);
        setServerResetStatus('error');
        // Continue with client reset even if server reset fails
      }
      
      // Step 2: Reset client state
      setClientResetStatus('pending');
      await new Promise(resolve => setTimeout(resolve, 500)); // Small delay for UI feedback
      
      // Clear all browser storage
      try {
        // Clear localStorage
        localStorage.clear();
        
        // Clear sessionStorage
        sessionStorage.clear();
        
        // Clear cookies by setting expiration in the past
        document.cookie.split(";").forEach(function(c) {
          document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
        });
        
        // Clear IndexedDB if needed (add additional cleanup if using)
        // This is a simplified example - in a real app you'd need to enumerate and close each DB
        const databases = await window.indexedDB.databases();
        databases.forEach(db => {
          try {
            window.indexedDB.deleteDatabase(db.name);
          } catch (e) {
            console.error('Error deleting IndexedDB:', e);
          }
        });
        
        setClientResetStatus('success');
      } catch (clientError) {
        console.error('Client reset error:', clientError);
        setClientResetStatus('error');
      }
      
      // Give a small delay before redirecting
      await new Promise(resolve => setTimeout(resolve, 1000));
      
    } catch (err) {
      console.error('Emergency reset error:', err);
      setError(err.message || 'Unknown error occurred');
    } finally {
      setIsResetting(false);
    }
  };
  
  const redirectToHome = () => {
    // Redirect to home with cache-busting parameter
    window.location.href = '/?reset=' + Date.now();
  };
  
  return (
    <Box 
      sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        bgcolor: '#f5f5f5'
      }}
    >
      <Paper 
        elevation={3} 
        sx={{ 
          p: 4, 
          width: '90%', 
          maxWidth: 600,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center'
        }}
      >
        <WarningIcon color="error" sx={{ fontSize: 64, mb: 2 }} />
        
        <Typography variant="h4" component="h1" align="center" gutterBottom>
          Emergency Reset Tool
        </Typography>
        
        <Typography variant="body1" align="center" paragraph sx={{ mb: 3 }}>
          This will completely reset the application state on both the client and server. 
          Use this if you are experiencing persistent issues with the application being stuck 
          in a loading state or other problems.
        </Typography>
        
        {error && (
          <Alert severity="error" sx={{ width: '100%', mb: 2 }}>{error}</Alert>
        )}
        
        {isResetting ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', my: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              {serverResetStatus === 'pending' ? (
                <CircularProgress size={20} sx={{ mr: 2 }} />
              ) : serverResetStatus === 'success' ? (
                <CheckCircleIcon color="success" sx={{ mr: 2 }} />
              ) : (
                <WarningIcon color="warning" sx={{ mr: 2 }} />
              )}
              <Typography>
                Server-side reset: {serverResetStatus === 'pending' ? 'In progress...' : 
                  serverResetStatus === 'success' ? 'Success' : 'Failed, but continuing...'}
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {clientResetStatus === 'pending' ? (
                <CircularProgress size={20} sx={{ mr: 2 }} />
              ) : clientResetStatus === 'success' ? (
                <CheckCircleIcon color="success" sx={{ mr: 2 }} />
              ) : clientResetStatus === 'error' ? (
                <WarningIcon color="warning" sx={{ mr: 2 }} />
              ) : (
                <Box sx={{ width: 20, mr: 2 }} />
              )}
              <Typography>
                Client-side reset: {clientResetStatus === 'pending' ? 'In progress...' : 
                  clientResetStatus === 'success' ? 'Success' : clientResetStatus === 'error' ? 'Failed' : 'Waiting...'}
              </Typography>
            </Box>
            
            {clientResetStatus === 'success' && (
              <Button 
                variant="contained" 
                color="primary" 
                sx={{ mt: 3 }}
                onClick={redirectToHome}
              >
                Reload Application
              </Button>
            )}
          </Box>
        ) : (
          <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
            <Button 
              variant="contained" 
              color="error" 
              size="large" 
              onClick={performEmergencyReset}
            >
              Perform Complete Reset
            </Button>
            
            <Button 
              variant="outlined"
              color="primary"
              size="large"
              onClick={redirectToHome}
            >
              Return to App
            </Button>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default EmergencyReset;
